import {
  CreateOrderResponse,
  InstrumentCategoryEnum,
  ListedInstrument,
  OpenOrder,
  OrderResponseType,
  OrderWarningsResponse,
  TempOrder,
  isDefined,
} from '@eih-netbroker/common';
import { JSX, useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-line.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useSnackbar } from '../../../../context/SnackbarContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { NbIconButton } from '../../../../shared/components/icon-button/IconButton';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useOpenOrders } from '../../../../shared/services/useOpenOrders';
import { useOptions } from '../../../../shared/services/useOptions';
import { useUserSettings } from '../../../../shared/services/user-settings/useUserSettings';
import { useNormalOrderSchema } from '../../schemas/orderSchemas';
import { useFuturesInfo } from '../../services/order/useFuturesInfo';
import { ValidatedOrder, useCreateNormalOrder } from '../../services/order/useNormalOrderView';
import { useOrderCurrencyExchange } from '../../services/order/useOrderCurrencyExchange';
import { useOrderTradingSession } from '../../services/order/useOrderTradingSession';
import { OrderExchangeValidationError, useOrderCurrencyExchangeState } from '../OrderCurrencyExchangeStateContext';
import { useOrderState } from '../OrderStateContext';
import { OrderConfirm } from './OrderConfirm';
import { OrderExchangeRateProgressBar } from './OrderExchangeRateProgressBar';
import { OrderCurrencyExchangeParams, OrderForm, OrderFormData } from './OrderForm';
import classes from './OrderModal.module.scss';
import { OrderModalBottomNavigation } from './OrderModalBottomNavigation';
import { OrderTwoFactorConfirm } from './OrderTwoFactorConfirm';
import { OrderWarningConfirm } from './OrderWarningConfirm';

export interface OrderModalProps {
  onCancel: (isCreated?: boolean) => void;
  initialView?: 'form' | 'confirm';
  order?: OrderFormData;
  orderId?: string;
  isWithCurrencyExchange?: boolean;
  openOrder?: OpenOrder;
}

export const OrderModal = ({
  onCancel,
  initialView: defaultContent = 'form',
  orderId,
  isWithCurrencyExchange,
  ...props
}: OrderModalProps) => {
  const { translation } = useLocale();
  const { setTempOrder, tempOrder, continueOrder, setWarnings, setSmsCode, setSmsCodeError, warnings, smsCode } =
    useOrderState();

  const { hasError } = useOrderCurrencyExchangeState();
  const [isOrderFormDetailsExpanded, setIsOrderFormDetailsExpanded] = useState(true);
  const { loadFuturesInitialMargin } = useFuturesInfo();
  const { getOrderDefaults } = useUserSettings();
  const [orderState, setOrderState] = useState<OrderFormData>({
    ...getOrderDefaults(props.order?.instrument, undefined),
    ...props.order,
  });

  const [isExchangeEnabled, setIsExchangeEnabled] = useState(
    Boolean(
      orderState.currencyExchangeParams && orderState.instrument?.instrumentCategory !== InstrumentCategoryEnum.Futures,
    ),
  );

  const hasExchangeError =
    isExchangeEnabled &&
    (hasError(OrderExchangeValidationError.NotEnoughCurrencyBalance) ||
      hasError(OrderExchangeValidationError.NotEnoughCurrencyBalanceToFullOrder));

  const initialOrder = useMemo(() => {
    if (props.order && orderId) {
      // when modifying an order, we want to keep the original order values
      // it must be ValidatedOrder to have all required fields
      return props.order as ValidatedOrder;
    }

    return null;
  }, [props.order, orderId]);

  const {
    createOrder,
    modifyOrder,
    isLoading,
    confirmOrder,
    validateLimitPrice,
    validateStopActivationPrice,
    priceValidationState,
    stopActivationPriceValidationState,
    generateSmsCode,
  } = useCreateNormalOrder();
  const { loadOrders } = useOpenOrders();
  const { selectedAccountOrClient } = useAccounts();
  const [isValid, setIsValid] = useState(false);

  const { showSuccess } = useSnackbar();
  const { getNormalOrderSchema } = useNormalOrderSchema();
  const { exchangeRate, baseInfo } = useOrderCurrencyExchangeState();
  const { getExchangeRateForOrder } = useOrderCurrencyExchange();

  const { getExpiryTypeOptions, getTradingSessionOptions } = useOptions();
  const { getInitialTradingSession } = useOrderTradingSession();

  const cleanOrderForm = useCallback(
    (orderForm: OrderFormData) => {
      const copyForm = { ...orderForm };
      if (!orderForm.orderType || !orderForm.instrument) {
        return orderForm;
      }

      const expiryTypeOptions = getExpiryTypeOptions(orderForm.instrument, orderForm.orderType);
      const tradingSessionOptions = getTradingSessionOptions(orderForm.instrument, orderForm.orderType);

      if (orderForm.expiryType && !expiryTypeOptions.some(e => e.id === orderForm.expiryType)) {
        copyForm.expiryType = undefined;
      }
      if (orderForm.tradingSession && !tradingSessionOptions.some(e => e.id === orderForm.tradingSession)) {
        copyForm.tradingSession = getInitialTradingSession(orderForm.instrument, orderForm.orderType);
      }

      return copyForm;
    },
    [getExpiryTypeOptions, getTradingSessionOptions, getInitialTradingSession],
  );

  const updateOrderState = useCallback(
    (order: OrderFormData) => {
      if (order.price !== orderState.price) {
        validateLimitPrice(order);
      }

      if (order.stopActivationPrice !== orderState.stopActivationPrice) {
        validateStopActivationPrice(order);
      }

      setOrderState(cleanOrderForm({ ...getOrderDefaults(order.instrument ?? null), ...order }));
    },
    [
      getOrderDefaults,
      orderState.price,
      validateLimitPrice,
      orderState.stopActivationPrice,
      validateStopActivationPrice,
      cleanOrderForm,
    ],
  );

  const changeInstrument = useCallback(
    (instrument: ListedInstrument | null) => {
      if (instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
        loadFuturesInitialMargin(instrument.ticker);
        setIsExchangeEnabled(false);
      }

      updateOrderState({ ...orderState, instrument });
    },
    [loadFuturesInitialMargin, orderState, updateOrderState],
  );

  const updateExchangeParams = useCallback((currencyExchangeParams: OrderCurrencyExchangeParams | undefined) => {
    setOrderState(cur => ({
      ...cur,
      currencyExchangeParams,
    }));
  }, []);

  const handleOnCancel = () => {
    onCancel();
  };

  const handleOnBack = () => {
    if ((warnings || tempOrder) && defaultContent === 'form') {
      setTempOrder(undefined);
      setWarnings(undefined);
      setSmsCode(undefined);
      setSmsCodeError(undefined);
      setIsOrderFormDetailsExpanded(!isExchangeEnabled);
    } else {
      onCancel();
    }
  };

  const handleCreateOrModifyOrderResponse = useCallback(
    async (order: CreateOrderResponse, isModify: boolean) => {
      if (order.$type === OrderResponseType.TempOrder) {
        setTempOrder(order as TempOrder);
      } else if (order.$type === OrderResponseType.Order) {
        showSuccess(isModify ? translation.orderModal.modifyOrderSuccess : translation.orderModal.createOrderSuccess);
        loadOrders(false);
        onCancel(true);
      } else if (order.$type === OrderResponseType.Warning) {
        setWarnings((order as OrderWarningsResponse).messages ?? []);
      }
    },
    [setTempOrder, showSuccess, translation.orderModal, onCancel, setWarnings, loadOrders],
  );

  const createOrModifyOrder = useCallback(
    async (validate: boolean) => {
      if (orderState && orderState.instrument) {
        if (orderId) {
          const order = await modifyOrder(
            orderId,
            orderState.instrument,
            orderState as ValidatedOrder,
            selectedAccountOrClient.account?.accountNumber!,
            validate,
          );

          handleCreateOrModifyOrderResponse(order, true);
          setIsOrderFormDetailsExpanded(false);
        } else {
          const order = await createOrder(
            orderState.instrument,
            orderState as ValidatedOrder,
            selectedAccountOrClient.account?.accountNumber!,
            validate,
          );

          if (order) {
            handleCreateOrModifyOrderResponse(order, false);
            setIsOrderFormDetailsExpanded(false);
          }
        }
      }
    },
    [
      createOrder,
      modifyOrder,
      orderState,
      orderId,
      selectedAccountOrClient,
      handleCreateOrModifyOrderResponse,
      setIsOrderFormDetailsExpanded,
    ],
  );

  const onContinue = useCallback(() => {
    continueOrder(async (twoFactor: boolean) => {
      if (warnings) {
        await createOrModifyOrder(false);
      } else {
        await confirmOrder(
          tempOrder?.tempOrderId!,
          twoFactor ? smsCode : undefined,
          !!orderId,
          orderState.currencyExchangeParams !== 'initialized' && !!orderState.currencyExchangeParams?.exchangeRate,
        );
        onCancel(true);
      }
    }, generateSmsCode);
  }, [
    orderState.currencyExchangeParams,
    continueOrder,
    confirmOrder,
    onCancel,
    tempOrder,
    warnings,
    createOrModifyOrder,
    smsCode,
    orderId,
    generateSmsCode,
  ]);

  useEffect(() => {
    try {
      getNormalOrderSchema(orderState).validateSync(orderState, {
        abortEarly: false,
      });
      setIsValid(
        !isDefined(priceValidationState.errorMessage) &&
          !priceValidationState.isLoading &&
          !isDefined(stopActivationPriceValidationState.errorMessage) &&
          !stopActivationPriceValidationState.isLoading,
      );
    } catch (e) {
      setIsValid(false);
    }
  }, [orderState, getNormalOrderSchema, priceValidationState, stopActivationPriceValidationState]);

  const initOrderScreen = useEffectEvent(() => {
    if (orderState.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
      loadFuturesInitialMargin(orderState.instrument.ticker);
    }
  });

  useEffect(() => {
    initOrderScreen();
  }, []);

  const onConfirmHandle = useCallback(async () => {
    createOrModifyOrder(true);
  }, [createOrModifyOrder]);

  const refreshExchangeRate = useCallback(async () => {
    if (!orderState.instrument || !orderState.price || typeof orderState.currencyExchangeParams !== 'object') {
      return;
    }

    if (tempOrder || warnings) {
      setTempOrder(undefined);
      setWarnings(undefined);
      // when we startted the order from widget, we need to close the modal
      if (defaultContent === 'confirm') {
        return onCancel(false);
      }
    }
    getExchangeRateForOrder(
      orderState.currencyExchangeParams.sourceCurrency,
      orderState.instrument.currency,
      orderState.currencyExchangeParams.targetAmount,
    );
  }, [orderState, getExchangeRateForOrder, setTempOrder, setWarnings, tempOrder, warnings, onCancel, defaultContent]);

  const content: { title: string; data: JSX.Element } | undefined = useMemo(() => {
    if (!tempOrder && !warnings) {
      return {
        title: translation.orderModal.order,
        data: (
          <OrderForm
            initialOrder={initialOrder}
            isModify={isDefined(orderId)}
            order={orderState}
            priceValidationState={priceValidationState}
            stopActivationPriceValidationState={stopActivationPriceValidationState}
            onChange={updateOrderState}
            onExchangeParamsChange={updateExchangeParams}
            onInstrumentChange={changeInstrument}
            isLoading={isLoading}
            isOrderFormDetailsExpanded={isOrderFormDetailsExpanded}
            isExchangeEnabled={isExchangeEnabled}
            onIsExchangeEnabledChange={setIsExchangeEnabled}
          />
        ),
      };
    }

    if (warnings) {
      return {
        title: translation.orderModal.order,
        data: <OrderWarningConfirm messages={warnings ?? []} />,
      };
    }

    if (tempOrder) {
      if (tempOrder.showWarningConfirmation) {
        return {
          title: translation.orderModal.order,
          data: <OrderWarningConfirm messages={tempOrder.messages ?? []} />,
        };
      }

      if (tempOrder.showConfirmation) {
        return {
          title: translation.orderModal.confirmOrder,
          data: (
            <OrderConfirm
              orderForm={orderState as ValidatedOrder}
              isWithCurrencyExchange={
                isWithCurrencyExchange ||
                (orderState.currencyExchangeParams !== 'initialized' &&
                  !!orderState.currencyExchangeParams?.targetAmount)
              }
            />
          ),
        };
      }

      if (tempOrder.showSmsConfirmation) {
        return {
          title: translation.orderModal.twoFactorConfirm.title,
          data: <OrderTwoFactorConfirm />,
        };
      }
    }
  }, [
    tempOrder,
    warnings,
    translation.orderModal.order,
    translation.orderModal.confirmOrder,
    translation.orderModal.twoFactorConfirm.title,
    orderId,
    isWithCurrencyExchange,
    orderState,
    priceValidationState,
    stopActivationPriceValidationState,
    updateOrderState,
    isLoading,
    initialOrder,
    updateExchangeParams,
    isOrderFormDetailsExpanded,
    isExchangeEnabled,
    changeInstrument,
  ]);

  return (
    <div className={classes.order}>
      <div className={classes.orderHeader}>
        <h4
          className={classes.orderTitle}
          data-testid={content?.title === translation.orderModal.confirmOrder ? 'order-confirm-title' : 'order-title'}
        >
          {content?.title}
        </h4>
        <NbIconButton
          onClick={() => onCancel()}
          icon={<CloseIcon />}
          disabled={isLoading}
          data-testid="order-modal-close-button"
        />
      </div>
      <div className={classes.orderContent}>
        {content?.data}

        {orderState.currencyExchangeParams && (
          <OrderExchangeRateProgressBar
            className={classes.orderExchangeRateProgressBar}
            refreshExchangeRate={refreshExchangeRate}
          />
        )}

        {!tempOrder && !warnings && (
          <div className={classes.orderButtons}>
            <NbButton
              color={'secondary'}
              onClick={handleOnCancel}
              disabled={isLoading}
              data-testid="order-modal-cancel-button"
            >
              {translation.general.cancel}
            </NbButton>
            <NbButton
              isLoading={isLoading}
              disabled={
                !isValid ||
                !orderState.instrument?.tradeable ||
                exchangeRate.isLoading ||
                baseInfo.isLoading ||
                (baseInfo.isLoaded && !exchangeRate.isLoaded) ||
                exchangeRate.isLoading ||
                hasExchangeError
              }
              onClick={onConfirmHandle}
              data-testid="order-modal-confirm-button"
            >
              {translation.general.send}
            </NbButton>
          </div>
        )}

        {(tempOrder || warnings) && (
          <OrderModalBottomNavigation
            initialView={defaultContent}
            isLoading={isLoading}
            tempOrder={tempOrder}
            warnings={warnings}
            onClose={() => onCancel(false)}
            onBack={handleOnBack}
            onContinue={onContinue}
          />
        )}
      </div>
    </div>
  );
};
